/**
 * Before After Image Block - Interactivity API View Script
 *
 * Provides reactive slider functionality using WordPress Interactivity API.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * Calculate position percentage from mouse/touch event.
 *
 * @param {Element} container  The container element.
 * @param {number}  clientX    Client X coordinate.
 * @param {number}  clientY    Client Y coordinate.
 * @param {string}  orientation Slider orientation.
 * @return {number} Position percentage (0-100).
 */
const calculatePosition = ( container, clientX, clientY, orientation ) => {
	const rect = container.getBoundingClientRect();
	let pos;

	if ( orientation === 'horizontal' ) {
		pos = ( ( clientX - rect.left ) / rect.width ) * 100;
	} else {
		pos = ( ( clientY - rect.top ) / rect.height ) * 100;
	}

	return Math.max( 0, Math.min( 100, pos ) );
};

/**
 * Before After Image block store.
 *
 * Uses WordPress Interactivity API for reactive state management.
 */
store( 'brandy/before-after-image', {
	/**
	 * Reactive state getters.
	 */
	state: {
		/**
		 * Get clip path for before image.
		 *
		 * @return {string} CSS clip-path value.
		 */
		get clipPath() {
			const ctx = getContext();
			return ctx.orientation === 'horizontal'
				? `inset(0 ${ 100 - ctx.position }% 0 0)`
				: `inset(0 0 ${ 100 - ctx.position }% 0)`;
		},

		/**
		 * Get slider left position (horizontal mode).
		 *
		 * @return {string|undefined} CSS left value or undefined.
		 */
		get sliderLeft() {
			const ctx = getContext();
			return ctx.orientation === 'horizontal'
				? `${ ctx.position }%`
				: undefined;
		},

		/**
		 * Get slider top position (vertical mode).
		 *
		 * @return {string|undefined} CSS top value or undefined.
		 */
		get sliderTop() {
			const ctx = getContext();
			return ctx.orientation === 'vertical'
				? `${ ctx.position }%`
				: undefined;
		},
	},

	/**
	 * Actions for user interactions.
	 */
	actions: {
		/**
		 * Start dragging on mouse down.
		 *
		 * @param {MouseEvent} event Mouse event.
		 */
		startDrag( event ) {
			// Only respond to primary mouse button.
			if ( event.button !== 0 ) {
				return;
			}

			event.preventDefault();
			const ctx = getContext();
			ctx.isDragging = true;

			const { ref } = getElement();
			ctx.position = calculatePosition(
				ref,
				event.clientX,
				event.clientY,
				ctx.orientation
			);
		},

		/**
		 * Handle mouse move during drag.
		 *
		 * @param {MouseEvent} event Mouse event.
		 */
		handleMove( event ) {
			const ctx = getContext();
			if ( ! ctx.isDragging ) {
				return;
			}

			event.preventDefault();
			const { ref } = getElement();
			ctx.position = calculatePosition(
				ref,
				event.clientX,
				event.clientY,
				ctx.orientation
			);
		},

		/**
		 * Start touch interaction.
		 *
		 * @param {TouchEvent} event Touch event.
		 */
		startTouch( event ) {
			const ctx = getContext();
			ctx.isDragging = true;

			const touch = event.touches[ 0 ];
			const { ref } = getElement();
			ctx.position = calculatePosition(
				ref,
				touch.clientX,
				touch.clientY,
				ctx.orientation
			);
		},

		/**
		 * Handle touch move.
		 *
		 * @param {TouchEvent} event Touch event.
		 */
		handleTouch( event ) {
			const ctx = getContext();
			if ( ! ctx.isDragging ) {
				return;
			}

			// Prevent page scroll while dragging.
			event.preventDefault();

			const touch = event.touches[ 0 ];
			const { ref } = getElement();
			ctx.position = calculatePosition(
				ref,
				touch.clientX,
				touch.clientY,
				ctx.orientation
			);
		},

		/**
		 * End drag/touch interaction.
		 */
		endDrag() {
			const ctx = getContext();
			ctx.isDragging = false;
		},

		/**
		 * Handle keyboard navigation.
		 *
		 * @param {KeyboardEvent} event Keyboard event.
		 */
		handleKeydown( event ) {
			const ctx = getContext();
			const step = event.shiftKey ? 10 : 1;
			let newPosition = ctx.position;

			switch ( event.key ) {
				case 'ArrowLeft':
				case 'ArrowUp':
					newPosition = ctx.position - step;
					break;
				case 'ArrowRight':
				case 'ArrowDown':
					newPosition = ctx.position + step;
					break;
				case 'Home':
					newPosition = 0;
					break;
				case 'End':
					newPosition = 100;
					break;
				default:
					return;
			}

			event.preventDefault();
			ctx.position = Math.max( 0, Math.min( 100, newPosition ) );
		},
	},
} );
